import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const PostSessionForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get('request_id');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [sessionData, setSessionData] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [editedSummary, setEditedSummary] = useState('');
  const [trainingTasks, setTrainingTasks] = useState(['']);
  const [sessionNotes, setSessionNotes] = useState('');

  const normalizeTasksForForm = (rawTasks) => {
    if (!rawTasks) return [];
    let parsed = rawTasks;
    if (typeof rawTasks === 'string') {
      try {
        parsed = JSON.parse(rawTasks);
      } catch (err) {
        parsed = [rawTasks];
      }
    }
    if (!Array.isArray(parsed)) {
      parsed = [parsed];
    }
    return parsed
      .map((task) => {
        if (typeof task === 'string') return task;
        if (task && typeof task === 'object') {
          return task.description || '';
        }
        return '';
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (!requestId) {
      setError('No session request ID provided');
      setLoading(false);
      return;
    }

    loadSessionData();
  }, [requestId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSessionRequestDetails(requestId);
      
      if (response.Code === 200) {
        setSessionData(response.request);

        const mockAiSummary = `Session Summary:\n\nDuring this ${response.request.duration_minutes || 30}-minute training session, we worked on:\n- Basic obedience commands\n- Leash training techniques\n- Addressing behavioral concerns\n\nThe client showed good progress and engagement throughout the session.`;

        if (response.session) {
          const existingSummary = response.session.session_summary || '';
          setAiSummary(existingSummary || mockAiSummary);
          setEditedSummary(existingSummary || mockAiSummary);

          const formTasks = normalizeTasksForForm(response.session.training_tasks);
          setTrainingTasks(formTasks.length > 0 ? formTasks : ['']);
          setSessionNotes(response.session.notes || '');
        } else {
          setAiSummary(mockAiSummary);
          setEditedSummary(mockAiSummary);
          setTrainingTasks(['']);
          setSessionNotes('');
        }
      } else {
        setError(response.Message || 'Failed to load session data');
      }
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setTrainingTasks([...trainingTasks, '']);
  };

  const handleRemoveTask = (index) => {
    if (trainingTasks.length > 1) {
      const newTasks = trainingTasks.filter((_, i) => i !== index);
      setTrainingTasks(newTasks);
    }
  };

  const handleTaskChange = (index, value) => {
    const newTasks = [...trainingTasks];
    newTasks[index] = value;
    setTrainingTasks(newTasks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate - at least one task must be filled
    const validTasks = trainingTasks
      .map(task => (task || '').trim())
      .filter(task => task.length > 0);
    if (validTasks.length === 0) {
      setError('Please add at least one training task for the client');
      return;
    }

    if (!editedSummary.trim()) {
      setError('Please provide a session summary');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const formattedTasks = validTasks.map(task => ({
        description: task,
        completed: false
      }));

      const updateData = {
        session_summary: editedSummary,
        training_tasks: formattedTasks,
        notes: sessionNotes
      };

      const response = await apiService.updateSessionFromRequest(requestId, updateData);

      if (response.Code === 200) {
        setSuccess(true);
        // Redirect to trainer portal after 2 seconds
        setTimeout(() => {
          navigate('/trainer');
        }, 2000);
      } else {
        setError(response.Message || 'Failed to save session artifacts');
      }
    } catch (err) {
      console.error('Error saving session artifacts:', err);
      setError('Failed to save session artifacts: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      padding: '20px',
      color: 'white'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      color: '#2c3e50'
    },
    header: {
      marginBottom: '30px',
      borderBottom: '2px solid #667eea',
      paddingBottom: '20px'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#667eea',
      marginBottom: '10px'
    },
    subtitle: {
      fontSize: '1rem',
      color: '#7f8c8d'
    },
    section: {
      marginBottom: '30px'
    },
    sectionTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    textarea: {
      width: '100%',
      minHeight: '150px',
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid #ddd',
      fontSize: '1rem',
      fontFamily: 'inherit',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    taskContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '10px',
      alignItems: 'flex-start'
    },
    taskInput: {
      flex: 1,
      padding: '10px',
      borderRadius: '8px',
      border: '2px solid #ddd',
      fontSize: '1rem',
      fontFamily: 'inherit'
    },
    button: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    addButton: {
      background: '#2ecc71',
      color: 'white',
      marginTop: '10px'
    },
    removeButton: {
      background: '#e74c3c',
      color: 'white',
      padding: '8px 15px',
      fontSize: '0.9rem'
    },
    submitButton: {
      background: 'linear-gradient(45deg, #667eea, #764ba2)',
      color: 'white',
      padding: '15px 40px',
      fontSize: '1.1rem',
      marginTop: '20px'
    },
    error: {
      background: '#fee',
      border: '2px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    success: {
      background: '#efe',
      border: '2px solid #2ecc71',
      color: '#27ae60',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      textAlign: 'center',
      fontSize: '1.1rem',
      fontWeight: '600'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#7f8c8d'
    },
    aiNote: {
      fontSize: '0.9rem',
      color: '#7f8c8d',
      fontStyle: 'italic',
      marginTop: '5px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.loading}>Loading session data...</div>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.error}>{error}</div>
          <button
            onClick={() => navigate('/trainer')}
            style={styles.button}
          >
            Back to Trainer Portal
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.success}>
            ✅ Session artifacts saved successfully! Redirecting to trainer portal...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Post-Session Artifacts</h1>
          <p style={styles.subtitle}>
            Complete the session documentation for your client
          </p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Session Summary Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span>📝</span>
              Session Summary
            </h2>
            <p style={styles.aiNote}>
              AI-generated summary (review and edit as needed):
            </p>
            <textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="Session summary will be generated automatically, but you can edit it here..."
              style={styles.textarea}
              required
            />
          </div>

          {/* Training Tasks Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span>✅</span>
              Training Tasks for Client
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '15px' }}>
              Add specific tasks or exercises for the client to work on before the next session
            </p>
            
            {trainingTasks.map((task, index) => (
              <div key={index} style={styles.taskContainer}>
                <input
                  type="text"
                  value={task}
                  onChange={(e) => handleTaskChange(index, e.target.value)}
                  placeholder={`Task ${index + 1} (e.g., "Practice sit command 10 times daily")`}
                  style={styles.taskInput}
                />
                {trainingTasks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTask(index)}
                    style={{ ...styles.button, ...styles.removeButton }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={handleAddTask}
              style={{ ...styles.button, ...styles.addButton }}
            >
              + Add Another Task
            </button>
          </div>

          {/* Additional Notes Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span>📋</span>
              Additional Notes (Optional)
            </h2>
            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="Any additional notes or observations..."
              style={{ ...styles.textarea, minHeight: '100px' }}
            />
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...styles.button,
                ...styles.submitButton,
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Session Artifacts'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostSessionForm;


